import { v4 as uuidv4 } from 'uuid';
import {
  ReportContext,
  ReportNode,
  SectionData,
  PageData,
  QuestionData,
} from './ReportUtils';
import { languageobj } from '@/i18n';
import SurveyUtils, {
  Section,
  Page,
  Question,
  QuestionParticipation,
  Survey,
  SurveyParticipation,
} from './SurveyUtils';
import AuthUtils from './AuthUtils';
import { SubOrganization } from './SubOrganizationUtils';
import cloneDeep from 'lodash.clonedeep';

const generateNodes = (
  context: ReportContext,
  items: any[],
  params?: {
    root?: boolean;
    documents?: boolean;
    resources?: boolean;
    labels?: boolean;
  },
  parent?: ReportNode,
  result: ReportNode[] = [],
  index: number = 0
) => {
  if (!items || items.length === 0) {
    return [];
  }

  // if this is the first element add some entry nodes
  if (!parent && index === 0) {
    if (params?.root === undefined || params?.root) {
      result.push(
        generateNode(
          {
            survey: context.survey,
            participation: context.surveyParticipation,
          },
          undefined,
          {
            display: languageobj('app.label.summary'),
            type: 'root',
            data: {
              visible: true,
            },
          }
        )
      );
    }

    if (
      (params?.documents === undefined || params?.documents) &&
      context.survey?.options?.documents?.value
    ) {
      result.push(
        generateNode(undefined, undefined, {
          display: context.survey?.options?.documents?.text,
          type: 'documents',
          data: {
            visible: true,
          },
        })
      );
    }

    if (
      (params?.resources === undefined || params?.resources) &&
      !!context.survey?.bookshelf?.id
    ) {
      result.push(
        generateNode(undefined, undefined, {
          display: languageobj('model.resource.label', { choice: 2 }),
          type: 'resources',
          data: {
            visible: true,
          },
        })
      );
    }
  }

  // labels
  if (
    (params?.labels === undefined || params?.labels) &&
    items[index].level !== undefined &&
    items[index].level !==
      (index === 0 ? parent?.item : items[index - 1])?.level
  ) {
    // add label if level is different than the previous node (previous node is the parent if index is 0)
    result.push(
      generateNode(undefined, undefined, {
        display:
          items[index].level === 'suborg'
            ? context.survey?.languageobjects?.suborg ||
              languageobj('model.survey.suborg')
            : context.survey?.languageobjects?.organization ||
              languageobj('model.survey.organization'),
        type: 'label',
        data: {
          visible: true,
        },
      })
    );
  }

  // generate the current node
  const current = generateNode(items[index], parent);

  // check for children and generate them if needed
  if (items[index].children || items[index].questions) {
    current.children = generateNodes(
      context,
      items[index].children || items[index].questions,
      params,
      current
    );

    // if there are no visible children, the current node must also be hidden
    current.data.visible = current.children?.some(
      (child) => child.data.visible
    );
  }

  result.push(current);

  // iterate over the next index
  if (index + 1 < items.length) {
    generateNodes(context, items, params, parent, result, index + 1);
  }

  return result;
};

const generateNode = (
  item: any,
  parent?: ReportNode,
  override?: Partial<ReportNode<any, any>>
) => {
  const node: Partial<ReportNode> = {
    uuid: uuidv4(),
    item: item,
    parent: parent,
    children: [],
  };

  if (item?.type === 'section') {
    Object.assign(node, {
      display: item.name,
      type: 'section',
      data: {
        visible: true,
        suborganization: undefined,
      },
    } as Partial<ReportNode<Section, SectionData>>);
  } else if (item?.type === 'page') {
    Object.assign(node, {
      display: item.shortname,
      type: 'page',
      data: {
        visible: true,
      },
    } as Partial<ReportNode<Page, PageData>>);
  } else if (item?.questionkey !== undefined) {
    Object.assign(node, {
      display: item.shorttext,
      type: 'question',
      data: {
        visible:
          (
            item.permission?.visible || ['oversight', 'serviceprovider']
          ).includes(AuthUtils.SessionStore.$session.role?.type) ||
          AuthUtils.SessionStore.$session.role?.type === 'superadmin' ||
          AuthUtils.SessionStore.$session.role?.type === 'admin',
        editable:
          (item.permission?.editable || ['serviceprovider']).includes(
            AuthUtils.SessionStore.$session.role?.type
          ) ||
          AuthUtils.SessionStore.$session.role?.type === 'superadmin' ||
          AuthUtils.SessionStore.$session.role?.type === 'admin',
        valid: true,
        dirty: false,
        participation: undefined,
        conditional: {
          visible: undefined,
        },
      },
    } as Partial<ReportNode<Question, QuestionData>>);
  }

  Object.assign(node, override);

  return node as ReportNode;
};

const generatePath = (target: ReportNode, nodes: ReportNode[]): number[] => {
  const index = nodes.findIndex((node) => node.uuid === target.uuid);
  if (index >= 0) {
    return [index + 1];
  } else {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].children.length > 0) {
        const children = generatePath(target, nodes[i].children);
        if (children.length > 0) {
          return [i + 1].concat(children);
        }
      }
    }
    return [];
  }
};

const getNode = (
  nodes: ReportNode[],
  test: (node: ReportNode) => boolean
): ReportNode | undefined => {
  for (const i in nodes) {
    if (test(nodes[i])) {
      return nodes[i];
    } else if (nodes[i].children.length > 0) {
      const node = getNode(nodes[i].children, test);
      if (node) {
        return node;
      }
    }
  }
};

const participations = new Map<string, Promise<QuestionParticipation>>();
const getParticipation = async (
  context: ReportContext,
  node: ReportNode<Question, QuestionData>,
  params?: { suborganization?: SubOrganization }
) => {
  const key = node.uuid + params?.suborganization?.id;
  let promise = participations.get(key);

  if (promise) {
    return await promise; // call already in progress
  } else {
    promise = new Promise<QuestionParticipation>(async (resolve, reject) => {
      try {
        node.data.participation = undefined;

        let participation: QuestionParticipation[];
        if (context.survey && context.surveyParticipation) {
          // API call only when a surveyparticipation exists
          participation = await SurveyUtils.api.questionParticipation.list({
            surveyid: context.survey?.id,
            surveyparticipationid: context.surveyParticipation?.id,
            questionid: node.item.id,
            suborganizationid: params?.suborganization?.id,
          });
        } else {
          participation = []; // set empty array so a blank participation will be added later
        }

        // ensure that the results match the passed suborganization just in case (undefined will find all)
        participation = participation?.filter(
          (item) => item.suborganization?.id == params?.suborganization?.id // use == so that it matches undefined and null
        );

        // if no participation, create blank participation
        if (participation.length <= 0) {
          participation[0] = {
            id: undefined,
            answer: {},
            completeness: 0,
            survey: context.survey as Survey,
            surveyparticipation: context.surveyParticipation as SurveyParticipation,
            suborganization: params?.suborganization,
            question: node.item,
          };
        }

        // JSON parser bug, backend will save {} as [], convert it back to {}
        if (
          !participation[0].answer ||
          Array.isArray(participation[0].answer)
        ) {
          participation[0].answer = {};
        }

        resolve(participation[0]);
      } catch (error) {
        reject(error);
      }
    });
  }

  participations.set(key, promise);
  node.data.participation = await promise;
  participations.delete(key);

  return node.data.participation;
};

const parsePath = (path: number[], nodes: ReportNode[]): ReportNode[] => {
  let result: ReportNode[] = [];
  const cloned = cloneDeep(path);
  const index = cloned.shift();
  if (index !== undefined && index < nodes.length) {
    result.push(nodes[index]);

    if (nodes[index].type === 'root') {
      // NOOP
    }

    if (nodes[index].type === 'label') {
      // clear the label that was just added and add the next section instead
      result.length = result.length - 1;
      result = result.concat(parsePath([index + 1].concat(cloned), nodes));
    }

    if (nodes[index].type === 'section') {
      if (cloned.length > 0 && nodes[index].children.length > 0) {
        result = result.concat(parsePath(cloned, nodes[index].children));
      } else if ((nodes[index].item as Section).toc) {
        // NOOP
      } else if (nodes[index].children.length > 0) {
        result = result.concat(parsePath([0], nodes[index].children));
      }
    }

    if (nodes[index].type === 'page') {
      result = result.concat(nodes[index].children); // all children are possibly visible for a page
    }

    if (nodes[index].type === 'question') {
      // NOOP
    }
  }
  return result;
};

const parsePathExact = (path: number[], nodes: ReportNode[]) => {
  const reducer = (result: ReportNode | undefined, index: number) => {
    return result && result.children ? result.children[index] : nodes[index];
  };
  return path.reduce(reducer, undefined);
};

export default {
  generateNodes,
  generateNode,
  generatePath,
  getNode,
  getParticipation,
  parsePath,
  parsePathExact,
};
